import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import {
    getLastBerthCallInPortCall,
    getPreviousPortCall,
    getBerthCallForSelection,
    getPreviousBerthCall,
    getLastBerthCallFromPrecedingPortCalls,
} from '../stowageRotationUtils';
import { getCargoContentsInBerthCall, getFilteredCargoContents, getCargoContentsInPortCall } from './cargoManifestUtils';
import { isPositive } from '../decimalsUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';

export const getCargoContentsForCondition = (
    stowagePlan: StowagePlan,
    selectedPortCall: RotationPortCall,
    selectedBerthCall: RotationBerthCall,
    selectedTankPlanCondition: TankPlanCondition
): CargoContent[] => {
    const berthCallForSelection = getBerthCallForSelection(stowagePlan, selectedPortCall, selectedBerthCall);
    if (!berthCallForSelection) {
        return;
    }

    let cargoContents: CargoContent[] = [];

    // Handle Arrival condition separately due to its complexity
    if (selectedTankPlanCondition === TankPlanCondition.Arrival) {
        const previousBerthCall = selectedBerthCall
            ? getPreviousBerthCall(stowagePlan, berthCallForSelection)
            : getLastBerthCallInPortCall(getPreviousPortCall(stowagePlan, selectedPortCall)) ??
              getLastBerthCallFromPrecedingPortCalls(stowagePlan, selectedPortCall);

        if (previousBerthCall) {
            // For Arrival, we need ALL cargo contents from the previous port call (including transit cargo)
            // Don't use getCargoContentsInBerthCall as it filters by berth call operations only
            const cargoContentsInPortCall = getCargoContentsInPortCall(stowagePlan, previousBerthCall.portCall);
            if (cargoContentsInPortCall.length) {
                // For Arrival, only include cargo contents with positive weights
                cargoContents = cargoContentsInPortCall.filter((c) => isPositive(c.weight));
            }
        }
        return cargoContents;
    }

    // Handle Departure condition - get all cargo from current port call (including transit)
    if (selectedTankPlanCondition === TankPlanCondition.Departure) {
        const cargoContentsInPortCall = getCargoContentsInPortCall(stowagePlan, selectedPortCall);
        if (cargoContentsInPortCall.length) {
            // For Departure, only include cargo contents with positive weights
            cargoContents = cargoContentsInPortCall.filter((c) => isPositive(c.weight));
        }
        return cargoContents;
    }

    // Handle other conditions with direct mapping
    const operationTypeMap: Record<TankPlanCondition, string[] | null> = {
        [TankPlanCondition.Departure]: null,
        [TankPlanCondition.OperationsInCall]: ['Loading', 'Unloading'],
        [TankPlanCondition.LoadingOperations]: ['Loading'],
        [TankPlanCondition.UnloadingOperations]: ['Unloading'],
        [TankPlanCondition.TransitOperations]: ['Transit'],
        [TankPlanCondition.Arrival]: [],
    };

    const operationTypes = operationTypeMap[selectedTankPlanCondition];
    return operationTypes
        ? getCargoContentForOperationsInternal(stowagePlan, selectedBerthCall, selectedPortCall, operationTypes)
        : getCargoContentsInBerthCall(stowagePlan, berthCallForSelection);
};

const getCargoContentForOperationsInternal = (
    stowagePlan: StowagePlan,
    selectedBerthCall: RotationBerthCall,
    selectedPortCall: RotationPortCall,
    operationTypes: string[]
) => {
    return selectedBerthCall
        ? getFilteredCargoContents(getCargoContentsInBerthCall(stowagePlan, selectedBerthCall), operationTypes)
        : getFilteredCargoContents(getCargoContentsInPortCall(stowagePlan, selectedPortCall), operationTypes);
};
